import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    useTheme as useMuiTheme,
    useMediaQuery,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button
} from '@mui/material';
import {
    Build as BuildIcon,
    Save as SaveIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchTariffCards, updateTariffCard } from '../../store/slices/settingsSlice';
import { useTheme } from '../../contexts/ThemeContext';
import { TariffCard as TariffCardType, TariffCardType as TariffType, UpdateTariffCardData, TariffCards } from '../../services/settingsApi';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

interface TariffFormData {
    same_city_500: string;
    same_zone_500: string;
    diff_zone_500: string;
    same_city_1: string;
    same_zone_1: string;
    diff_zone_1: string;
    same_city_2: string;
    same_zone_2: string;
    diff_zone_2: string;
    same_city_3: string;
    same_zone_3: string;
    diff_zone_3: string;
    same_city_extra: string;
    same_zone_extra: string;
    diff_zone_extra: string;
}

const WEIGHT_LABELS: { [key: string]: string } = {
    '500': '0.0 to 0.5 KG',
    '1000': '0.5 to 1 KG',
    '2000': '1 to 2 KG',
    '3000': '2 to 3 KG',
    'extra': 'Each Extra KG'
};

const TARIFF_CONFIGS: { type: TariffType; title: string; key: keyof TariffCards }[] = [
    { type: 'overnight-cod', title: 'Overnight COD Tariffs', key: 'overnight_cod' },
    { type: 'overnight-prepaid', title: 'Overnight Prepaid Tariffs', key: 'overnight_prepaid' },
    { type: 'express-cod', title: 'Express COD Tariffs', key: 'express_cod' },
    { type: 'express-prepaid', title: 'Express Prepaid Tariffs', key: 'express_prepaid' },
];

const convertCardsToFormData = (cards: TariffCardType[]): TariffFormData => {
    const formData: TariffFormData = {
        same_city_500: '', same_zone_500: '', diff_zone_500: '',
        same_city_1: '', same_zone_1: '', diff_zone_1: '',
        same_city_2: '', same_zone_2: '', diff_zone_2: '',
        same_city_3: '', same_zone_3: '', diff_zone_3: '',
        same_city_extra: '', same_zone_extra: '', diff_zone_extra: '',
    };

    cards.forEach((card) => {
        const suffix = card.weight === '1000' ? '1' : card.weight === '2000' ? '2' : card.weight === '3000' ? '3' : card.weight;
        formData[`same_city_${suffix}` as keyof TariffFormData] = card.same_city?.toString() || '';
        formData[`same_zone_${suffix}` as keyof TariffFormData] = card.same_zone?.toString() || '';
        formData[`diff_zone_${suffix}` as keyof TariffFormData] = card.diff_zone?.toString() || '';
    });

    return formData;
};

const TariffCardPage = () => {
    const muiTheme = useMuiTheme();
    const { theme: appTheme } = useTheme();
    const dispatch = useAppDispatch();
    const { tariffCards, tariffLoading, error } = useAppSelector((state) => state.settings);
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const { toast, showSuccess, showError, hideToast } = useToast();

    const [formData, setFormData] = useState<{ [key: string]: TariffFormData }>({});
    const [savingType, setSavingType] = useState<string | null>(null);
    const [expandedPanels, setExpandedPanels] = useState<string[]>(['overnight-cod']);

    useEffect(() => {
        dispatch(fetchTariffCards());
    }, [dispatch]);

    useEffect(() => {
        if (tariffCards) {
            const newFormData: { [key: string]: TariffFormData } = {};
            TARIFF_CONFIGS.forEach((config) => {
                const cards = tariffCards[config.key as keyof typeof tariffCards] || [];
                newFormData[config.type] = convertCardsToFormData(cards as TariffCardType[]);
            });
            setFormData(newFormData);
        }
    }, [tariffCards]);

    const handleInputChange = (type: string, field: keyof TariffFormData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                [field]: value
            }
        }));
    };

    const handleSubmit = async (type: TariffType) => {
        setSavingType(type);
        try {
            const data = formData[type];
            const updateData: UpdateTariffCardData = {
                same_city_500: parseFloat(data.same_city_500) || 0,
                same_zone_500: parseFloat(data.same_zone_500) || 0,
                diff_zone_500: parseFloat(data.diff_zone_500) || 0,
                same_city_1: parseFloat(data.same_city_1) || 0,
                same_zone_1: parseFloat(data.same_zone_1) || 0,
                diff_zone_1: parseFloat(data.diff_zone_1) || 0,
                same_city_2: parseFloat(data.same_city_2) || 0,
                same_zone_2: parseFloat(data.same_zone_2) || 0,
                diff_zone_2: parseFloat(data.diff_zone_2) || 0,
                same_city_3: parseFloat(data.same_city_3) || 0,
                same_zone_3: parseFloat(data.same_zone_3) || 0,
                diff_zone_3: parseFloat(data.diff_zone_3) || 0,
                same_city_extra: parseFloat(data.same_city_extra) || 0,
                same_zone_extra: parseFloat(data.same_zone_extra) || 0,
                diff_zone_extra: parseFloat(data.diff_zone_extra) || 0,
            };

            await dispatch(updateTariffCard({ type, data: updateData })).unwrap();
            showSuccess(`${type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} tariffs updated successfully`);
        } catch (err: any) {
            showError(err?.message || 'Failed to update tariffs');
        } finally {
            setSavingType(null);
        }
    };

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanels((prev) =>
            isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
        );
    };

    const renderTariffTable = (type: TariffType, title: string) => {
        const data = formData[type] || {} as TariffFormData;

        const rows = [
            { weight: '500', label: WEIGHT_LABELS['500'] },
            { weight: '1000', label: WEIGHT_LABELS['1000'] },
            { weight: '2000', label: WEIGHT_LABELS['2000'] },
            { weight: '3000', label: WEIGHT_LABELS['3000'] },
            { weight: 'extra', label: WEIGHT_LABELS['extra'] },
        ];

        return (
            <Accordion
                expanded={expandedPanels.includes(type)}
                onChange={handleAccordionChange(type)}
                sx={{
                    mb: 2,
                    bgcolor: muiTheme.palette.background.paper,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    '&:before': { display: 'none' }
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        bgcolor: muiTheme.palette.action.hover,
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center'
                        }
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: muiTheme.palette.action.hover }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Weight</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Within City</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Same Zone</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Diff. Zone</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const suffix = row.weight === '1000' ? '1' : row.weight === '2000' ? '2' : row.weight === '3000' ? '3' : row.weight;
                                    return (
                                        <TableRow key={row.weight}>
                                            <TableCell>{row.label}</TableCell>
                                            <TableCell align="center">
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={data[`same_city_${suffix}` as keyof TariffFormData] || ''}
                                                    onChange={(e) => handleInputChange(type, `same_city_${suffix}` as keyof TariffFormData, e.target.value)}
                                                    inputProps={{ min: 0, step: 1 }}
                                                    sx={{ width: 100 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={data[`same_zone_${suffix}` as keyof TariffFormData] || ''}
                                                    onChange={(e) => handleInputChange(type, `same_zone_${suffix}` as keyof TariffFormData, e.target.value)}
                                                    inputProps={{ min: 0, step: 1 }}
                                                    sx={{ width: 100 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={data[`diff_zone_${suffix}` as keyof TariffFormData] || ''}
                                                    onChange={(e) => handleInputChange(type, `diff_zone_${suffix}` as keyof TariffFormData, e.target.value)}
                                                    inputProps={{ min: 0, step: 1 }}
                                                    sx={{ width: 100 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={savingType === type ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={() => handleSubmit(type)}
                            disabled={savingType !== null}
                            sx={{
                                bgcolor: appTheme.palette.secondary.main,
                                '&:hover': {
                                    bgcolor: appTheme.palette.secondary.dark,
                                }
                            }}
                        >
                            {savingType === type ? 'Updating...' : 'Update'}
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
        );
    };

    if (tariffLoading && !tariffCards) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    sx={{
                        color: appTheme.palette.text.primary,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <BuildIcon />
                    Tariff Card
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Configure shipping prices by weight and delivery zone
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Tariff Tables */}
            {TARIFF_CONFIGS.map((config) => (
                <React.Fragment key={config.type}>
                    {renderTariffTable(config.type, config.title)}
                </React.Fragment>
            ))}

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default TariffCardPage;
